/**
 * Onboarding Persona Wizard — T4 (P1 follow-up dla Izy Bury, 2026-05-27).
 *
 * Pierwszy login (lub `user.preferences.persona == null`) → force-redirect tutaj.
 * 2 ścieżki:
 *   - seller_led: sprzedawca LED / dystrybutor (default, dotychczasowa)
 *   - designer:   projektant wnętrz / architekt / studio (nowa, walidowana
 *                  zgłoszeniami Izy e638ae66 + 0475d485)
 *
 * Po wyborze: PATCH /api/auth/preferences/persona → /dashboard.
 * Skip: nie ma — wybór jest obowiązkowy (refresh strony NIE pomija).
 *
 * Per FOUNDERS_JOURNAL 2026-05-26 "Hybrid persona MASKIL":
 *   ETA 2 tygodnie do 2026-06-09. To jest etap 1 (wybór), reszta UX delta
 *   (sidebar items, RFQ templates, glossary tags) — kolejna sesja.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Briefcase, Palette, ArrowRight, Check, Loader2,
  FileText, Building2, Lightbulb, Compass,
} from 'lucide-react';
import { setPersona } from '../lib/api';

const PERSONAS = [
  {
    key: 'seller_led',
    icon: Briefcase,
    title: 'Sprzedawca · Dystrybutor · Producent',
    subtitle: 'B2B sprzedaż produktów i rozwiązań',
    description:
      'Twoja codzienna praca to wygrywanie przetargów BZP/TED i dostarczanie produktów do biznesów, samorządów, instytucji.',
    perks: [
      { icon: FileText, text: 'Przetargi BZP/TED/Licytacje na pierwszej linii' },
      { icon: Lightbulb, text: 'Pre-Tender — sygnały 1-6 miesięcy przed ogłoszeniem' },
      { icon: Briefcase, text: 'ProfitRoom — bid/no-bid analiza z marżą' },
      { icon: Compass, text: 'Adam pisze odpowiedzi na SWZ z perspektywy oferenta' },
    ],
    badge: 'Domyślna ścieżka',
    color: 'emerald',
  },
  {
    key: 'designer',
    icon: Palette,
    title: 'Projektant · Architekt · Studio wnętrz',
    subtitle: 'Specyfikuję oświetlenie w projektach',
    description:
      'Twoja codzienna praca to projektowanie wnętrz/budynków i wybieranie najlepszych opraw dla klientów. Szukasz dostawców, nie kupujesz w przetargach.',
    perks: [
      { icon: Building2, text: 'GUNB — Twoje projekty + sieć powiązanych' },
      { icon: Palette, text: 'Katalog 234k opraw z parametrami (UGR, CRI, IP, DALI)' },
      { icon: Lightbulb, text: 'Pre-Tender — projekty przed pozwoleniem (planowanie zaopatrzenia)' },
      { icon: Compass, text: 'Adam pomaga w RFQ z perspektywy zamawiającego specs' },
    ],
    badge: 'Nowa ścieżka · beta',
    color: 'indigo',
  },
];

export default function OnboardingPersonaWizard({ user, onPersonaSet }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!selected) {
      toast.error('Wybierz jedną z opcji — to ustawi UI dopasowane do Twojej pracy');
      return;
    }
    setSubmitting(true);
    try {
      const result = await setPersona(selected);
      toast.success(
        selected === 'seller_led'
          ? 'Ustawione: Sprzedawca. Witaj w MASKIL!'
          : 'Ustawione: Projektant. Witaj w MASKIL! Tryb beta — daj nam znać co usprawnić.'
      );
      // Powiadom App.js żeby reload user state + ukryć wizard
      if (typeof onPersonaSet === 'function') {
        onPersonaSet(result);
      }
      // Bez wymuszenia reload — App.js przekieruje na podstawie zmienionego user.preferences
      setTimeout(() => {
        navigate('/my-dashboard', { replace: true });
      }, 600);
    } catch (e) {
      toast.error(
        'Nie udało się zapisać wyboru: ' +
          (e.response?.data?.detail || e.message)
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 bg-gradient-to-br from-background to-muted/30">
      <div className="max-w-5xl w-full space-y-6">
        <div className="text-center space-y-2">
          <h1
            className="text-3xl md:text-4xl font-semibold tracking-tight"
            style={{ fontFamily: 'Space Grotesk' }}
          >
            Witaj{user?.name ? `, ${user.name}` : ''} — czym się zajmujesz?
          </h1>
          <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
            MASKIL pracuje dla 2 typów użytkowników. Wybierz ścieżkę, a UI dopasuje
            się do Twojej codziennej pracy. Możesz to zmienić później w Ustawieniach.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PERSONAS.map((p) => {
            const Icon = p.icon;
            const isSelected = selected === p.key;
            const colorClasses =
              p.color === 'emerald'
                ? {
                    border: isSelected ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-border',
                    iconBg: 'bg-emerald-500/10 text-emerald-500',
                    badge: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
                  }
                : {
                    border: isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/30' : 'border-border',
                    iconBg: 'bg-indigo-500/10 text-indigo-500',
                    badge: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30',
                  };
            return (
              <Card
                key={p.key}
                className={`cursor-pointer transition-all hover:shadow-lg ${colorClasses.border}`}
                onClick={() => setSelected(p.key)}
                data-testid={`persona-card-${p.key}`}
              >
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className={`p-3 rounded-xl ${colorClasses.iconBg}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    {isSelected && (
                      <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg md:text-xl">{p.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{p.subtitle}</p>
                  </div>
                  <Badge variant="outline" className={`text-xs w-fit ${colorClasses.badge}`}>
                    {p.badge}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm">{p.description}</p>
                  <div className="space-y-2 pt-2 border-t border-border">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Co zobaczysz w MASKIL:
                    </p>
                    {p.perks.map((perk, i) => {
                      const PIcon = perk.icon;
                      return (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <PIcon className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span>{perk.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-2">
          <p className="text-xs text-muted-foreground text-center md:text-left">
            {selected ? (
              <>
                Wybrałeś:{' '}
                <span className="font-medium">
                  {PERSONAS.find((p) => p.key === selected)?.title}
                </span>
              </>
            ) : (
              <>Najedź na kartę i kliknij, aby wybrać ścieżkę.</>
            )}
          </p>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={!selected || submitting}
            className="gap-2 min-w-[180px]"
            data-testid="persona-wizard-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Zapisuję…
              </>
            ) : (
              <>
                Rozpocznij pracę
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>

        <p className="text-[11px] text-center text-muted-foreground/70">
          Wybór można zmienić w Ustawieniach. Tryb projektanta to wczesna beta —
          będziemy aktywnie zbierać feedback w pierwszych 30 dniach po wdrożeniu.
        </p>
      </div>
    </div>
  );
}
